import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../../Layout/Layout';
import LocationsManager from '../../Locations/LocationsManager';
import { useIsRestaurant } from '../../../hooks/useAccountType';
import ScheduleStatusBanner from './ScheduleStatusBanner';
import DateRangeMode from '../../../components/schedule/DateRangeMode';
import CopyScheduleDialog from '../../../components/schedule/CopyScheduleDialog';
import { findOverlaps } from '../../../components/schedule/overlaps';
import { useGetVideoWalls } from '../../../queries/videoWall';
import { useGetWallGroups } from '../../../queries/wallGroups';
import { useGetLocations } from '../../../queries/locations';
import { useGetPlaylists } from '../../../queries/wallPlaylistLibrary';
import {
  useGetAssignments, useCreateAssignment, useUpdateAssignment, useDeleteAssignment,
  useCopyAssignment,
} from '../../../queries/wallAssignments';
import { useInHub } from '../../../nav/useInHub';
import SubTabs from '../../../nav/SubTabs';

/**
 * Phase 6 — Visual weekly Scheduler for video walls and wall groups.
 *
 * Pick a wall (or a whole group), then drag time blocks onto a 7-day × 24-hour
 * timeline to assign which library playlist plays when. Each block is one
 * playlist_assignment (start_time / end_time / days_of_week mask / priority);
 * the backend resolves the active block in each location's timezone and the
 * per-minute `video-wall:apply-schedule` job auto-switches playing walls at the
 * window boundary. When no block matches, walls fall back to their default
 * (is_active) playlist — restaurants never go blank.
 *
 *   - Drag on an empty area of a day column → create a block (editor opens).
 *   - Click a block → edit times / days / playlist / priority, or delete.
 */

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']; // bit 0..6
const HOUR_PX = 30;            // pixel height of one hour row
const DAY_MIN = 24 * 60;
const SNAP_MIN = 15;           // drag snaps to quarter-hour
const TOTAL_PX = 24 * HOUR_PX;

// Distinct, readable block colors — chosen by a stable hash of the playlist id so
// the same playlist keeps its color across the grid.
const PALETTE = [
  '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899',
  '#14B8A6', '#F97316', '#6366F1', '#84CC16', '#06B6D4',
  '#D946EF', '#EF4444', '#22C55E', '#EAB308', '#0EA5E9',
];
const colorFor = (id) => {
  let h = 0;
  for (let i = 0; i < String(id).length; i += 1) h = (h * 31 + String(id).charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

const timeToMin = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(':');
  return (parseInt(h, 10) * 60) + parseInt(m, 10);
};
const minToInput = (min) => {
  const h = Math.floor(min / 60); const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
const snap = (min) => Math.max(0, Math.min(DAY_MIN, Math.round(min / SNAP_MIN) * SNAP_MIN));

// Local 'YYYY-MM-DD' (no tz shift) for a Date picked in the date-range editor.
const fmtDateLocal = (dateObj) => {
  if (!dateObj) return null;
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const parseDateLocal = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`) : null);

// Days an assignment applies to (mask null/0 = every day).
const daysOf = (mask) => (!mask ? [0, 1, 2, 3, 4, 5, 6] : DAY_KEYS.map((_, i) => i).filter((i) => mask & (1 << i)));

// Visual segment(s) for an assignment on a given day. All-day → full column;
// a window that wraps past midnight splits into two segments.
const segmentsFor = (a) => {
  const s = timeToMin(a.start_time);
  const e = timeToMin(a.end_time);
  if (s === null || e === null) return [[0, DAY_MIN]];
  if (e > s) return [[s, e]];
  return [[s, DAY_MIN], [0, e]]; // wraps midnight
};

/** Calendar-style lane layout so overlapping blocks in a day sit side by side. */
const layoutDay = (segs) => {
  const sorted = [...segs].sort((x, y) => x.start - y.start || x.end - y.end);
  const out = [];
  let cluster = []; let clusterEnd = -1;
  const flush = () => {
    if (!cluster.length) return;
    const lanes = []; // lane index -> last end
    cluster.forEach((seg) => {
      let placed = false;
      for (let i = 0; i < lanes.length; i += 1) {
        if (seg.start >= lanes[i]) { seg.lane = i; lanes[i] = seg.end; placed = true; break; }
      }
      if (!placed) { seg.lane = lanes.length; lanes.push(seg.end); }
    });
    cluster.forEach((seg) => { seg.laneCount = lanes.length; out.push(seg); });
    cluster = []; clusterEnd = -1;
  };
  sorted.forEach((seg) => {
    if (cluster.length && seg.start >= clusterEnd) flush();
    cluster.push(seg); clusterEnd = Math.max(clusterEnd, seg.end);
  });
  flush();
  return out;
};

export default function VideoWallSchedule() {
  const { t } = useTranslation();
  const inHub = useInHub();
  const navigate = useNavigate();
  const isRestaurant = useIsRestaurant();

  const { data: walls = [] } = useGetVideoWalls();
  const { data: groups = [] } = useGetWallGroups();
  const { data: locations = [] } = useGetLocations();
  const { data: playlists = [] } = useGetPlaylists();

  // Preselect a target when arrived via "Manage Schedule" (?wall=… or ?group=…).
  const [searchParams] = useSearchParams();
  const initialGroup = searchParams.get('group');
  const initialWall = searchParams.get('wall');
  const [targetType, setTargetType] = useState(initialGroup ? 'group' : 'wall'); // 'wall' | 'group'
  const [wallId, setWallId] = useState(initialWall || null);
  const [groupId, setGroupId] = useState(initialGroup || null);
  const targetId = targetType === 'group' ? groupId : wallId;

  const queryParams = targetType === 'group'
    ? (groupId ? { group_id: groupId } : null)
    : (wallId ? { wall_id: wallId } : null);
  const { data: assignments = [] } = useGetAssignments(queryParams || {});

  const create = useCreateAssignment();
  const update = useUpdateAssignment();
  const del = useDeleteAssignment();
  const copy = useCopyAssignment();

  const gridRef = useRef(null);
  const [view, setView] = useState('schedule'); // 'schedule' | 'locations'
  const [drag, setDrag] = useState(null);      // { dayIndex, y0, y1 } while dragging
  const [editor, setEditor] = useState(null);  // editor dialog state
  const [lastSaved, setLastSaved] = useState(null); // { allDay, time } → reassurance strip
  const [copyOpen, setCopyOpen] = useState(false);

  // Conflict detection: wall-direct rows are more specific (0) than group rows (1).
  const overlaps = useMemo(
    () => findOverlaps(assignments, (r) => (r.assignable_type === 'video_wall' ? 0 : 1)),
    [assignments],
  );

  const wallOptions = walls.map((w) => ({ label: `${w.name} · ${w.rows}×${w.columns}`, value: w.id }));
  const groupOptions = groups.map((g) => ({
    label: `${g.name} · ${g.rows ? `${g.rows}×${g.columns}` : t('video_wall.groups.any_geometry')} · ${t('video_wall.groups.walls_count', { n: g.walls_count ?? 0 })}`,
    value: g.id,
  }));
  const playlistOptions = playlists.map((p) => ({ label: p.name, value: p.id }));
  const playlistName = useMemo(() => {
    const m = {}; playlists.forEach((p) => { m[p.id] = p.name; }); return m;
  }, [playlists]);

  // Timezone note for the selected target.
  const tzNote = useMemo(() => {
    if (targetType === 'group') return t('video_wall.schedule.tz_group');
    const w = walls.find((x) => x.id === wallId);
    if (!w) return null;
    const loc = locations.find((l) => l.id === w.location_id);
    return loc?.timezone
      ? t('video_wall.schedule.tz_wall', { tz: loc.timezone })
      : t('video_wall.schedule.tz_default');
  }, [targetType, wallId, walls, locations, t]);

  // Build per-day, laid-out blocks from the assignments.
  const dayBlocks = useMemo(() => {
    const perDay = DAY_KEYS.map(() => []);
    assignments.forEach((a) => {
      const segs = segmentsFor(a);
      daysOf(a.days_of_week).forEach((di) => {
        segs.forEach(([start, end]) => {
          if (end <= start) return;
          perDay[di].push({ a, start, end, lane: 0, laneCount: 1 });
        });
      });
    });
    return perDay.map((segs) => layoutDay(segs));
  }, [assignments]);

  // ── drag-to-create ──────────────────────────────────────────────────────
  const yToMin = (clientY) => {
    const rect = gridRef.current.getBoundingClientRect();
    return snap(((clientY - rect.top) / rect.height) * DAY_MIN);
  };
  const onColPointerDown = (di, e) => {
    if (!targetId) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const m = yToMin(e.clientY);
    setDrag({ dayIndex: di, y0: m, y1: m });
  };
  const onColPointerMove = (e) => {
    if (!drag) return;
    setDrag((d) => ({ ...d, y1: yToMin(e.clientY) }));
  };
  const onColPointerUp = () => {
    if (!drag) return;
    const start = Math.min(drag.y0, drag.y1);
    let end = Math.max(drag.y0, drag.y1);
    if (end - start < SNAP_MIN) end = Math.min(DAY_MIN, start + SNAP_MIN); // a tap = 15-min block
    setEditor({
      id: null, playlistId: playlistOptions[0]?.value ?? null, allDay: false,
      start: minToInput(start), end: minToInput(end), priority: 0,
      range: { mode: 'weekly', daysMask: 1 << drag.dayIndex, startDate: null, endDate: null },
    });
    setDrag(null);
  };

  const openBlock = (a) => setEditor({
    id: a.id, playlistId: a.playlist_id,
    allDay: !a.start_time || !a.end_time,
    start: a.start_time ? String(a.start_time).slice(0, 5) : '08:00',
    end: a.end_time ? String(a.end_time).slice(0, 5) : '12:00',
    priority: a.priority || 0,
    range: a.start_date
      ? { mode: 'range', daysMask: a.days_of_week || 0, startDate: parseDateLocal(a.start_date), endDate: parseDateLocal(a.end_date || a.start_date) }
      : { mode: 'weekly', daysMask: a.days_of_week || 0, startDate: null, endDate: null },
  });

  // Map the editor's recurrence (weekly | date-range) → API date/day fields.
  const rangeToFields = (r) => {
    if (r.mode === 'range') {
      return {
        days_of_week: r.daysMask || null,            // null = every day in range
        start_date: fmtDateLocal(r.startDate),
        end_date: fmtDateLocal(r.endDate || r.startDate),
      };
    }
    return { days_of_week: r.daysMask === 0 ? null : r.daysMask, start_date: null, end_date: null };
  };

  const saveEditor = async () => {
    const ed = editor;
    if (!ed.playlistId) return;
    if (ed.range?.mode === 'range' && !ed.range?.startDate) return; // need a start date
    const schedule = {
      start_time: ed.allDay ? null : `${ed.start}:00`,
      end_time: ed.allDay ? null : `${ed.end}:00`,
      ...rangeToFields(ed.range),
      priority: ed.priority,
    };
    if (ed.id) {
      await update.mutateAsync({ id: ed.id, playlist_id: ed.playlistId, ...schedule });
    } else {
      await create.mutateAsync({
        playlist_id: ed.playlistId,
        ...(targetType === 'group' ? { group_id: groupId } : { wall_id: wallId }),
        ...schedule,
      });
    }
    setLastSaved({ allDay: ed.allDay, time: ed.start });
    setEditor(null);
  };

  const removeEditor = () => {
    const id = editor.id;
    setEditor(null);
    confirmDialog({
      message: t('video_wall.schedule.delete_confirm'),
      header: t('video_wall.schedule.delete_title'),
      icon: 'pi pi-exclamation-triangle', acceptClassName: 'p-button-danger',
      acceptLabel: t('video_wall.schedule.delete'), rejectLabel: t('video_wall.schedule.cancel'),
      accept: () => del.mutate(id),
    });
  };

  // Copy-to-other-targets candidates (exclude the current target).
  const copyTargets = useMemo(() => {
    const ws = wallOptions
      .filter((o) => !(targetType === 'wall' && o.value === wallId))
      .map((o) => ({ label: `🖥️ ${o.label}`, value: o.value, type: 'video_wall' }));
    const gs = groupOptions
      .filter((o) => !(targetType === 'group' && o.value === groupId))
      .map((o) => ({ label: `👥 ${o.label}`, value: o.value, type: 'wall_group' }));
    return [...ws, ...gs];
  }, [wallOptions, groupOptions, targetType, wallId, groupId]);

  const applyCopy = (dests) => copy.mutateAsync({
    from: { type: targetType === 'group' ? 'wall_group' : 'video_wall', id: targetId },
    to: dests,
    mode: 'replace',
  });

  return (
    <Layout>
      <ConfirmDialog />
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        {!inHub && (
        <div>
          <h2 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>
            <i className="pi pi-calendar me-2" style={{ color: 'var(--pp-primary)' }} />
            {t('video_wall.schedule.title')}
          </h2>
          <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.schedule.subtitle')}</p>
        </div>
        )}
        {!isRestaurant && <Button label={t('video_wall.groups.back_to_walls')} icon="pi pi-th-large" outlined onClick={() => navigate('/apps/video-wall/walls')} />}
      </div>

      {/* Tabs: Schedule | Locations */}
      {inHub ? (
        <SubTabs ariaLabel={t('video_wall.schedule.title')} items={[
          { id: 'schedule', label: t('video_wall.schedule.title'), active: view === 'schedule', onClick: () => setView('schedule') },
        ]} />
      ) : (
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button label={t('video_wall.schedule.title')} icon="pi pi-calendar" size="small" outlined={view !== 'schedule'} onClick={() => setView('schedule')} />
        <Button label={t('video_wall.locations.title')} icon="pi pi-map-marker" size="small" outlined={view !== 'locations'} onClick={() => setView('locations')} />
      </div>
      )}

      {view === 'locations' && <LocationsManager showWalls />}

      {view === 'schedule' && (<>
      {/* Target picker */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button label={t('video_wall.library.assign.target_wall')} size="small"
          outlined={targetType !== 'wall'} onClick={() => setTargetType('wall')} />
        <Button label={t('video_wall.library.assign.target_group')} size="small"
          outlined={targetType !== 'group'} onClick={() => setTargetType('group')} />
        {targetType === 'wall' ? (
          <Dropdown value={wallId} options={wallOptions} onChange={(e) => setWallId(e.value)}
            placeholder={t('video_wall.library.assign.pick_wall')} style={{ minWidth: 260 }} filter />
        ) : (
          <Dropdown value={groupId} options={groupOptions} onChange={(e) => setGroupId(e.value)}
            placeholder={t('video_wall.library.assign.pick_group')} style={{ minWidth: 260 }} filter
            emptyMessage={t('video_wall.library.assign.no_groups')} />
        )}
        {tzNote && (
          <small style={{ color: 'var(--pp-text-secondary)' }}>
            <i className="pi pi-clock me-1" />{tzNote}
          </small>
        )}
      </div>

      {!targetId ? (
        <div className="text-center p-5" style={{ background: 'var(--pp-bg-secondary)', border: '1px dashed var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
          <div style={{ fontSize: '3rem' }}>🗓️</div>
          <h4 className="mt-3" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.schedule.pick_target_title')}</h4>
          <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.schedule.pick_target_subtitle')}</p>
        </div>
      ) : (
        <>
          {/* Now playing / next transition */}
          <ScheduleStatusBanner type={targetType} id={targetId} />

          {/* Toolbar: copy-to-other-targets + overlap warning */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            {overlaps.count > 0 ? (
              <small style={{ color: 'var(--pp-warning)' }}>
                <i className="pi pi-exclamation-triangle me-1" />
                {t('video_wall.schedule.conflicts', { n: overlaps.count })}
              </small>
            ) : <span />}
            <Button label={t('video_wall.schedule.copy_button')} icon="pi pi-copy" size="small" outlined
              disabled={assignments.length === 0} onClick={() => setCopyOpen(true)} />
          </div>

          {/* Save confirmation — reassure that no Start/Refresh is needed */}
          {lastSaved && (
            <div className="p-2 mb-3 d-flex align-items-center justify-content-between gap-2"
              style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid var(--pp-success)', borderRadius: 'var(--pp-radius-md)' }}>
              <small style={{ color: 'var(--pp-text-primary)' }}>
                <i className="pi pi-check-circle me-2" style={{ color: 'var(--pp-success)' }} />
                {lastSaved.allDay
                  ? t('video_wall.schedule.saved_default')
                  : t('video_wall.schedule.saved_at', { time: lastSaved.time })}
              </small>
              <i className="pi pi-times" style={{ cursor: 'pointer', color: 'var(--pp-text-secondary)' }} onClick={() => setLastSaved(null)} />
            </div>
          )}

          {playlistOptions.length === 0 && (
            <div className="p-3 mb-3" style={{ background: 'var(--pp-bg-secondary)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-md)' }}>
              <small style={{ color: 'var(--pp-text-secondary)' }}>
                <i className="pi pi-info-circle me-1" />{t('video_wall.schedule.no_playlists')}
              </small>
            </div>
          )}
          <small className="d-block mb-2" style={{ color: 'var(--pp-text-secondary)' }}>
            <i className="pi pi-info-circle me-1" />{t('video_wall.schedule.drag_hint')}
            {' '}<strong>{t('video_wall.schedule.auto_apply')}</strong>
          </small>

          {/* Weekly grid */}
          <div style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)', overflow: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `48px repeat(7, minmax(90px, 1fr))`, minWidth: 720 }}>
              {/* Header row */}
              <div style={{ borderBottom: '1px solid var(--pp-border)' }} />
              {DAY_KEYS.map((d) => (
                <div key={d} className="text-center py-2" style={{ borderBottom: '1px solid var(--pp-border)', borderInlineStart: '1px solid var(--pp-border)', color: 'var(--pp-text-primary)', fontWeight: 600, fontSize: 13 }}>
                  {t(`video_wall.library.assign.day_${d}`)}
                </div>
              ))}

              {/* Hour gutter */}
              <div style={{ position: 'relative', height: TOTAL_PX }}>
                {[...Array(24)].map((_, h) => (
                  <div key={h} style={{ position: 'absolute', top: h * HOUR_PX - 7, right: 4, fontSize: 10, color: 'var(--pp-text-secondary)' }}>
                    {String(h).padStart(2, '0')}:00
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {DAY_KEYS.map((d, di) => (
                <div
                  key={d}
                  ref={di === 0 ? gridRef : undefined}
                  onPointerDown={(e) => onColPointerDown(di, e)}
                  onPointerMove={onColPointerMove}
                  onPointerUp={onColPointerUp}
                  style={{ position: 'relative', height: TOTAL_PX, borderInlineStart: '1px solid var(--pp-border)', cursor: targetId ? 'crosshair' : 'default', touchAction: 'none' }}
                >
                  {/* hour gridlines */}
                  {[...Array(24)].map((_, h) => (
                    <div key={h} style={{ position: 'absolute', top: h * HOUR_PX, left: 0, right: 0, borderTop: '1px solid var(--pp-border)', opacity: h % 6 === 0 ? 0.9 : 0.35 }} />
                  ))}

                  {/* assignment blocks */}
                  {dayBlocks[di].map((seg, idx) => {
                    const top = (seg.start / DAY_MIN) * TOTAL_PX;
                    const height = Math.max(14, ((seg.end - seg.start) / DAY_MIN) * TOTAL_PX);
                    const widthPct = 100 / seg.laneCount;
                    const c = colorFor(seg.a.playlist_id);
                    const isShadowed = overlaps.shadowed.has(seg.a.id);
                    return (
                      <div
                        key={`${seg.a.id}-${idx}`}
                        onPointerDown={(e) => { e.stopPropagation(); }}
                        onClick={(e) => { e.stopPropagation(); openBlock(seg.a); }}
                        title={`${seg.a.playlist?.name || playlistName[seg.a.playlist_id] || ''}${isShadowed ? ` — ${t('video_wall.schedule.shadowed_hint')}` : ''}`}
                        style={{
                          position: 'absolute', top, height,
                          left: `calc(${seg.lane * widthPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`,
                          background: `${c}26`, borderInlineStart: `3px solid ${c}`,
                          border: isShadowed ? '1px dashed var(--pp-warning)' : `1px solid ${c}80`,
                          opacity: isShadowed ? 0.6 : 1,
                          borderRadius: 6, padding: '2px 5px', overflow: 'hidden', cursor: 'pointer', fontSize: 11, color: 'var(--pp-text-primary)',
                        }}
                      >
                        <div className="text-truncate" style={{ fontWeight: 600 }}>
                          {isShadowed && <i className="pi pi-exclamation-triangle me-1" style={{ fontSize: 9, color: 'var(--pp-warning)' }} />}
                          {seg.a.playlist?.name || playlistName[seg.a.playlist_id] || t('video_wall.schedule.playlist')}
                        </div>
                        <div className="text-truncate" style={{ color: 'var(--pp-text-secondary)', fontSize: 10 }}>
                          {(!seg.a.start_time || !seg.a.end_time)
                            ? t('video_wall.library.assign.all_day')
                            : `${String(seg.a.start_time).slice(0, 5)}–${String(seg.a.end_time).slice(0, 5)}`}
                          {seg.a.priority ? ` · P${seg.a.priority}` : ''}
                        </div>
                      </div>
                    );
                  })}

                  {/* drag preview */}
                  {drag && drag.dayIndex === di && (() => {
                    const a = Math.min(drag.y0, drag.y1); const b = Math.max(drag.y0, drag.y1);
                    return (
                      <div style={{
                        position: 'absolute', left: 2, right: 2,
                        top: (a / DAY_MIN) * TOTAL_PX, height: Math.max(2, ((b - a) / DAY_MIN) * TOTAL_PX),
                        background: 'var(--pp-btn-honey)', opacity: 0.25, border: '1px dashed var(--pp-btn-honey)', borderRadius: 6, pointerEvents: 'none',
                      }} />
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      </>)}

      {/* Block editor */}
      <Dialog
        visible={!!editor}
        onHide={() => setEditor(null)}
        header={editor?.id ? t('video_wall.schedule.edit_block') : t('video_wall.schedule.new_block')}
        style={{ width: 'min(95vw, 480px)' }}
      >
        {editor && (
          <div className="d-flex flex-column gap-3 pt-2">
            <div>
              <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.schedule.playlist')}</label>
              <Dropdown value={editor.playlistId} options={playlistOptions} onChange={(e) => setEditor((ed) => ({ ...ed, playlistId: e.value }))}
                placeholder={t('video_wall.schedule.pick_playlist')} className="w-100" filter />
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <Button label={t('video_wall.library.assign.all_day')} size="small"
                outlined={!editor.allDay} onClick={() => setEditor((ed) => ({ ...ed, allDay: true }))} />
              <Button label={t('video_wall.library.assign.scheduled')} size="small"
                outlined={editor.allDay} onClick={() => setEditor((ed) => ({ ...ed, allDay: false }))} />
            </div>

            {!editor.allDay && (
              <div className="d-flex align-items-center gap-2">
                <small style={{ color: 'var(--pp-text-secondary)', width: 44 }}>{t('video_wall.library.assign.from')}</small>
                <input type="time" step="900" value={editor.start} onChange={(e) => setEditor((ed) => ({ ...ed, start: e.target.value }))} />
                <small style={{ color: 'var(--pp-text-secondary)', width: 24 }}>{t('video_wall.library.assign.to')}</small>
                <input type="time" step="900" value={editor.end} onChange={(e) => setEditor((ed) => ({ ...ed, end: e.target.value }))} />
              </div>
            )}

            <DateRangeMode
              value={editor.range}
              onChange={(range) => setEditor((ed) => ({ ...ed, range }))}
              dayLabels={DAY_KEYS.map((d) => t(`video_wall.library.assign.day_${d}`))}
              labels={{
                weekly: t('video_wall.schedule.repeat_weekly'),
                range: t('video_wall.schedule.date_range'),
                from: t('video_wall.library.assign.from'),
                to: t('video_wall.library.assign.to'),
                daysHint: t('video_wall.library.assign.days_hint'),
                rangeHint: t('video_wall.schedule.range_hint'),
                rangeDaysHint: t('video_wall.schedule.range_days_hint'),
              }}
            />

            <div className="d-flex align-items-center gap-2">
              <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.library.assign.priority')}</small>
              <InputNumber value={editor.priority} min={0} max={999} onValueChange={(e) => setEditor((ed) => ({ ...ed, priority: e.value || 0 }))} inputStyle={{ width: 80 }} />
              <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.library.assign.priority_hint')}</small>
            </div>

            <div className="d-flex justify-content-between align-items-center gap-2 pt-2" style={{ borderTop: '1px solid var(--pp-border)' }}>
              {editor.id ? (
                <Button label={t('video_wall.schedule.delete')} icon="pi pi-trash" text severity="danger" onClick={removeEditor} />
              ) : <span />}
              <div className="d-flex gap-2">
                <Button label={t('video_wall.schedule.cancel')} text onClick={() => setEditor(null)} />
                <Button label={t('video_wall.schedule.save')} icon="pi pi-check"
                  loading={create.isLoading || update.isLoading}
                  disabled={!editor.playlistId || (editor.range?.mode === 'range' && !editor.range?.startDate)}
                  onClick={saveEditor} />
              </div>
            </div>
          </div>
        )}
      </Dialog>

      <CopyScheduleDialog
        visible={copyOpen}
        onHide={() => setCopyOpen(false)}
        title={t('video_wall.schedule.copy_title')}
        sourceLabel={t('video_wall.schedule.copy_source', { n: assignments.length })}
        replaceWarning={t('video_wall.schedule.copy_replace_warning')}
        targets={copyTargets}
        onApply={applyCopy}
        loading={copy.isLoading}
      />
    </Layout>
  );
}
